import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { lumi } from '../lib/lumi';
import { toast } from 'react-hot-toast';

interface AuthContextType {
  user: any | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<any | null>(lumi.auth.user);
  const [isAuthenticated, setIsAuthenticated] = useState(lumi.auth.isAuthenticated);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange(({ isAuthenticated, user }) => {
      setUser(user);
      setIsAuthenticated(isAuthenticated);
      setIsLoading(false);
    });
    
    // Initial check
    setIsLoading(false);
    
    return unsubscribe;
  }, []);

  const signIn = async () => {
    try {
      await lumi.auth.signIn();
      toast.success('Successfully signed in!');
    } catch (error: any) {
      console.error('Login failed:', error);
      toast.error('Login failed. Please try again.');
    }
  };

  const signOut = async () => {
    try {
      await lumi.auth.signOut();
      toast.success('Signed out successfully');
    } catch (error: any) {
      console.error('Logout failed:', error);
      toast.error('Logout failed');
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
